import React, { useEffect, useRef, useState, useCallback } from 'react';
import classNames from 'classnames';
import './SubcategoriesActions.css';
import { SubcategoriesActionsSorting } from '../SubcategoriesActionsSorting';
import { useSortingOptionAvailability } from '../../../hooks/use-sorting-option-availability';
import { useFilterOptionAvailability } from '../../../hooks/use-filter-option-availability.hook';
import { SubcategoriesFilterAction } from '../SubcategoriesFilterAction';
import { useDeviceTypeHook } from '../../../../../shared/src/hooks/use-device-type.hook';
import {
    GtmEventLocation,
    GtmEventComponent,
    GtmEventClickElement
} from '../../../../../shared/src/components/google-tag-manager/enums/gtm-event-enum';
import { GtmService } from '../../../../../shared/src/services/gtm.service';
import { useCurrentVerticalData } from '../../../../../shared/src/hooks/use-current-vertical-data.hook';

export const SubcategoriesActions = ({
    verticalRef,
    scrollableItemsElement,
    currentSubcategory,
}) => {
    const actionsRef = useRef();
    const { current: verticalEl } = verticalRef;
    const { current: actionsEl } = actionsRef;
    const { current: scrollableItemsEl } = scrollableItemsElement;
    const [scrolledOut, setScrolledOut] = useState(false);
    const { isMobile } = useDeviceTypeHook();

    const { isAnySortingOptionAvailable: sortingEnabled } = useSortingOptionAvailability();
    const { isAnyFilterOptionAvailable: filteringEnabled } = useFilterOptionAvailability();
    const verticalElTopRef = useRef(null);
    const isAnimatingRef = useRef(false);
    const [sortingOpened, setSortingOpened] = useState(false);
    const [filteringOpened, setFilteringOpened] = useState(false);
    const [isAnyDialogOpened, setIsAnyDialogOpened] = useState(false);

    useEffect(() => {
        setIsAnyDialogOpened(sortingOpened || filteringOpened);
    }, [sortingOpened, filteringOpened]);

    const {
        pamGameVerticalID,
        currentVerticalType,
    } = useCurrentVerticalData();

    const trackSubcategories = useCallback((component, clickElement, clickAction) => {
        GtmService.track({
            location: GtmEventLocation.GAME_GRID,
            component,
            click_element: clickElement,
            click_action: clickAction,
            game_vertical_id: pamGameVerticalID,
            game_vertical_type: currentVerticalType,
        });
    }, [pamGameVerticalID, currentVerticalType]);

    const trackSubcategoriesSorting = useCallback((isOpen) => {
        const clickAction = isOpen ? 'open sorting' : 'close sort';
        trackSubcategories(GtmEventComponent.SORTING, GtmEventClickElement.ICON, clickAction);
    }, [trackSubcategories]);

    const trackSubcategoriesFiltering = useCallback((isOpen) => {
        const clickAction = isOpen ? 'open filters' : 'close filters';
        trackSubcategories(GtmEventComponent.FILTERS, GtmEventClickElement.ICON, clickAction);
    }, [trackSubcategories]);

    const closeAll = useCallback(() => {
        if (sortingOpened) {
            trackSubcategoriesSorting(false);
            setSortingOpened(false);
        }

        if (filteringOpened) {
            trackSubcategoriesFiltering(false);
            setFilteringOpened(false);
        }
    }, [sortingOpened, filteringOpened, trackSubcategoriesSorting, trackSubcategoriesFiltering]);

    const handleSortingToggle = useCallback((isOpen) => {
        if (filteringOpened) {
            setFilteringOpened(false);
        }
        trackSubcategoriesSorting(isOpen);
        setSortingOpened(isOpen);
    }, [filteringOpened, trackSubcategoriesSorting]);

    const handleFilteringToggle = useCallback((isOpen) => {
        if (sortingOpened) {
            setSortingOpened(false);
        }
        trackSubcategoriesFiltering(isOpen);
        setFilteringOpened(isOpen);
    }, [sortingOpened, trackSubcategoriesFiltering]);

    const updateActionsVisibility = useCallback(() => {
        if (!isAnyDialogOpened && verticalEl && actionsEl && scrollableItemsEl && !isAnimatingRef.current) {
            const { top: verticalTop } = verticalEl.getBoundingClientRect();
            if (verticalElTopRef.current !== verticalTop) {
                const { top: actionsTop, left: actionsLeft } = actionsEl.getBoundingClientRect();
                const { right: scrollableItemsRight } = scrollableItemsEl.getBoundingClientRect();
                const scrolledToBottomDirection = verticalElTopRef.current !== null
                    && verticalElTopRef.current < verticalTop;
                verticalElTopRef.current = verticalTop;
                setScrolledOut(
                    !scrolledToBottomDirection
                    && actionsTop >= verticalTop
                    && scrollableItemsRight > actionsLeft
                );
            }
        }
    }, [verticalEl, actionsEl, scrollableItemsEl, isAnyDialogOpened]);

    useEffect(() => {
        isAnimatingRef.current = true;
        // reset animating flag after css animation is completed (175ms + some safe gap)
        setTimeout(() => {
            isAnimatingRef.current = false;
        }, 200);
    }, [scrolledOut]);

    // Actions need to be hidden once main content is scrolled up
    useEffect(() => {
        const events = ['scroll', 'resize'];
        events.forEach((eventName) => {
            window.addEventListener(eventName, updateActionsVisibility, {
                passive: true,
            });
        });

        updateActionsVisibility();

        return () => {
            events.forEach((eventName) => {
                window.removeEventListener(eventName, updateActionsVisibility);
            });
        };
    }, [updateActionsVisibility]);

    const className = classNames(
        'asg-subcategories__actions',
        {
            'asg-subcategories__actions--scrolled-out': scrolledOut,
            'asg-subcategories__actions--dialog-opened': isAnyDialogOpened,
        },
    );

    return (
        <div className={className}
            ref={actionsRef}>
            {isAnyDialogOpened && !isMobile &&
                <div className="asg-subcategories__actions-dialog-backdrop"
                    onClick={closeAll}
                    role="button"
                    tabIndex="0"
                ></div>
            }
            {sortingEnabled && <div className="asg-subcategories__actions-item">
                <SubcategoriesActionsSorting
                    onToggle={handleSortingToggle}
                    opened={sortingOpened}
                />
            </div>}
            {filteringEnabled && <div className="asg-subcategories__actions-item">
                <SubcategoriesFilterAction
                    onToggle={handleFilteringToggle}
                    opened={filteringOpened}
                    currentSubcategory={currentSubcategory}
                />
            </div>}
        </div>
    );
};
